import React, { useState, useEffect } from 'react';
import { Movie, StreamingData, movieApi } from '../services/api';
import { posterService, MoviePoster } from '../services/posterApi';
import { useAuth } from '../contexts/AuthContext';

interface MovieCardProps {
  movie: Movie;
  onClick?: () => void;
  showSimilarity?: boolean;
  showStreaming?: boolean;
  rank?: number;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  showExplanation?: boolean;
  showWatchlistButton?: boolean;
}

const MovieCard: React.FC<MovieCardProps> = ({
  movie,
  onClick,
  showSimilarity = false,
  showStreaming = true,
  rank,
  className = '',
  size = 'medium',
  showExplanation = false,
  showWatchlistButton = true
}) => {
  const { isAuthenticated } = useAuth();
  const [poster, setPoster] = useState<MoviePoster | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [streamingData, setStreamingData] = useState<StreamingData | null>(null);
  const [isLoadingStreaming, setIsLoadingStreaming] = useState(true);
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [isWatchlistLoading, setIsWatchlistLoading] = useState(false);
  const [showExplanationModal, setShowExplanationModal] = useState(false);

  useEffect(() => {
    loadPoster();
    if (showStreaming) {
      loadStreamingData();
    }
    if (isAuthenticated && showWatchlistButton) {
      checkWatchlistStatus();
    } else {
      setIsInWatchlist(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movie.id, movie.cleaned_title, movie.year, movie.imdb_id, showStreaming, isAuthenticated, showWatchlistButton]);

  const loadPoster = async () => {
    setIsLoading(true);
    setImageError(false);
    
    try {
      const moviePoster = await posterService.getMoviePoster(movie.id);
      setPoster(moviePoster);
    } catch (error) {
      console.error('Failed to load poster:', error);
      setImageError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStreamingData = async () => {
    setIsLoadingStreaming(true);

    try {
      const data = await movieApi.getMovieStreaming(movie.id);
      setStreamingData(data);
    } catch (error) {
      console.error('Failed to load streaming data:', error);
      setStreamingData(null);
    } finally {
      setIsLoadingStreaming(false);
    }
  };

  const checkWatchlistStatus = async () => {
    try {
      const response = await movieApi.getWatchlist();
      const isInList = response.watchlist.some(item => item.id === movie.id);
      setIsInWatchlist(isInList);
    } catch (error) {
      console.error('Failed to check watchlist status:', error);
      setIsInWatchlist(false);
    }
  };

  const handleImageError = () => {
    setImageError(true);
    setPoster({
      posterUrl: `https://via.placeholder.com/300x450/374151/f9fafb?text=${encodeURIComponent(movie.omdb_title || movie.cleaned_title)}`,
      source: 'placeholder'
    });
  };

  const cardSizes = {
    small: 'max-w-xs',
    medium: 'max-w-sm',
    large: 'max-w-md'
  };

  const handleWatchlistToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      alert('Please sign in to use the watchlist feature');
      return;
    }

    setIsWatchlistLoading(true);

    try {
      if (isInWatchlist) {
        await movieApi.removeFromWatchlist(movie.id);
        setIsInWatchlist(false);
      } else {
        await movieApi.addToWatchlist(movie.id);
        setIsInWatchlist(true);
      }
    } catch (error) {
      console.error('Watchlist operation failed:', error);
      alert('Failed to update watchlist. Please try again.');
    } finally {
      setIsWatchlistLoading(false);
    }
  };

  return (
    <div
      className={`relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer transform hover:scale-105 ${cardSizes[size]} ${className}`}
      onClick={onClick}
    >
      {/* Rank Badge */}
      {rank && (
        <div className="absolute top-2 left-2 z-10 bg-blue-600 text-white text-sm font-bold px-2 py-1 rounded-full shadow-md">
          #{rank}
        </div>
      )}

      {/* Similarity Badge - positioned next to rank badge */}
      {showSimilarity && movie.similarity_score && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (showExplanation && (movie.explanation || movie.shared_features)) {
              setShowExplanationModal(true);
            }
          }}
          className={`absolute top-2 z-10 bg-purple-600 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-md transition-all duration-200 ${
            rank ? 'left-14' : 'left-2'
          } ${showExplanation && (movie.explanation || movie.shared_features) ? 'hover:bg-purple-700 cursor-pointer' : 'cursor-default'}`}
          style={showExplanation && (movie.explanation || movie.shared_features) ? {
            boxShadow: '0 0 0 2px rgb(250 204 21)',
            animation: 'pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          } : undefined}
          title={showExplanation && (movie.explanation || movie.shared_features) ? "Click to see why this was recommended" : undefined}
        >
          {(movie.similarity_score * 100).toFixed(1)}%
        </button>
      )}

      {/* Watchlist Button */}
      {showWatchlistButton && isAuthenticated && (
        <button
          onClick={handleWatchlistToggle}
          disabled={isWatchlistLoading}
          className={`absolute top-2 right-2 z-10 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110 ${
            isInWatchlist
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
          } ${isWatchlistLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={isInWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
        >
          {isWatchlistLoading ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75"></path>
            </svg>
          ) : isInWatchlist ? (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          )}
        </button>
      )}

      {/* Poster Section */}
      <div className="relative w-full bg-gray-200 overflow-hidden" style={{ aspectRatio: '2/3' }}>
        {/* Poster Image */}
        <div className="w-full h-full">
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : poster?.posterUrl && !imageError ? (
            <img
              src={poster.posterUrl}
              alt={`${movie.omdb_title || movie.cleaned_title} poster`}
              className="w-full h-full object-cover"
              onError={handleImageError}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 text-gray-500">
              <div className="text-center p-4">
                <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
                <p className="text-xs font-medium">No Poster</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Movie Info Section */}
      <div className="p-4">
        {/* Title */}
        <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 hover:text-blue-600 transition-colors">
          {movie.omdb_title || movie.cleaned_title}
        </h3>

        {/* Year and Director */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
          {movie.year && (
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              {movie.year}
            </span>
          )}
          {movie.director && (
            <span className="flex items-center gap-1 truncate">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              <span className="truncate">{movie.director}</span>
            </span>
          )}
        </div>

        {/* Runtime */}
        {movie.runtime && (
          <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            {movie.runtime}
          </div>
        )}

        {/* Ratings */}
        <div className="flex flex-wrap gap-1 mb-3">
          {movie.imdb_rating && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
              ⭐ {movie.imdb_rating}
            </span>
          )}
          {movie.rotten_tomatoes && (
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
              🍅 {movie.rotten_tomatoes}
            </span>
          )}
          {movie.metacritic && (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
              📊 {movie.metacritic}
            </span>
          )}
        </div>

        {/* Plot Preview */}
        {movie.plot && movie.plot !== "NO MATCH FOUND" && (
          <p className="text-gray-700 text-sm line-clamp-3 mb-3">
            {movie.plot.length > 100 ? `${movie.plot.substring(0, 100)}...` : movie.plot}
          </p>
        )}

        {/* Genres */}
        {movie.genres && movie.genres.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {movie.genres.slice(0, 3).map((genre, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
              >
                {genre}
              </span>
            ))}
            {movie.genres.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                +{movie.genres.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Streaming Availability */}
        {showStreaming && (
          <div className="mt-3">
            {isLoadingStreaming ? (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                <span>Loading streaming...</span>
              </div>
            ) : streamingData && streamingData.streaming_options.length > 0 ? (
              <div>
                <div className="text-xs font-medium text-gray-700 mb-2">Available on:</div>
                <div className="flex flex-wrap gap-1">
                  {streamingData.streaming_options.slice(0, 4).map((option, index) => (
                    <span
                      key={index}
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        option.type === 'subscription' 
                          ? 'bg-green-100 text-green-800' 
                          : option.type === 'rent'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                      title={option.price ? `${option.name} - ${option.price}` : option.name}
                    >
                      {option.name}
                      {option.type !== 'subscription' && option.price && (
                        <span className="ml-1 opacity-75">({option.price})</span>
                      )}
                    </span>
                  ))}
                  {streamingData.streaming_options.length > 4 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                      +{streamingData.streaming_options.length - 4} more
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-xs text-gray-500">
                Streaming info unavailable
              </div>
            )}
          </div>
        )}

        {/* Similarity Progress Bar */}
        {showSimilarity && movie.similarity_score && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Similarity</span>
              <span>{(movie.similarity_score * 100).toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-purple-400 to-purple-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${(movie.similarity_score * 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Explanation Modal */}
      {showExplanationModal && (movie.explanation || movie.shared_features) && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            e.stopPropagation();
            setShowExplanationModal(false);
          }}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-100 p-2 rounded-full">
                    <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 6.343a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464a1 1 0 10-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.477.859h4z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Why This Recommendation?</h3>
                    <p className="text-sm text-gray-600">For "{movie.omdb_title || movie.cleaned_title}"</p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowExplanationModal(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Shared Features */}
              {(movie.shared_features || movie.explanation?.shared_features) && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Shared Features:</h4>
                  <div className="flex flex-wrap gap-2">
                    {(movie.shared_features || movie.explanation?.shared_features || []).map((feature, index) => {
                      const [type, ...nameParts] = feature.split('_');
                      const name = nameParts.join(' ');
                      
                      const getFeatureColor = (type: string) => {
                        switch(type) {
                          case 'genre': return 'bg-blue-100 text-blue-800';
                          case 'actor': return 'bg-green-100 text-green-800';
                          case 'director': return 'bg-purple-100 text-purple-800';
                          case 'decade': return 'bg-orange-100 text-orange-800';
                          case 'rating': return 'bg-yellow-100 text-yellow-800';
                          default: return 'bg-gray-100 text-gray-800';
                        }
                      };

                      const getFeatureIcon = (type: string) => {
                        switch(type) {
                          case 'genre': return '🎭';
                          case 'actor': return '👤';
                          case 'director': return '🎬';
                          case 'decade': return '📅';
                          case 'rating': return '⭐';
                          default: return '🏷️';
                        }
                      };

                      return (
                        <span
                          key={index}
                          className={`px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${getFeatureColor(type)}`}
                        >
                          <span>{getFeatureIcon(type)}</span>
                          <span className="capitalize">{name}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Similarity Breakdown */}
              {movie.explanation?.similarities && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Similarity Breakdown:</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                        Content Similarity
                      </span>
                      <span className="text-sm font-semibold">
                        {((movie.explanation.similarities.content || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${((movie.explanation.similarities.content || 0) * 100)}%` }}
                      ></div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                        Script Similarity
                      </span>
                      <span className="text-sm font-semibold">
                        {((movie.explanation.similarities.script || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${((movie.explanation.similarities.script || 0) * 100)}%` }}
                      ></div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                        Video Similarity
                      </span>
                      <span className="text-sm font-semibold">
                        {((movie.explanation.similarities.video || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${((movie.explanation.similarities.video || 0) * 100)}%` }}
                      ></div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <span className="w-3 h-3 bg-pink-500 rounded-full"></span>
                        Audio Similarity
                      </span>
                      <span className="text-sm font-semibold">
                        {((movie.explanation.similarities.audio || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-pink-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${((movie.explanation.similarities.audio || 0) * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Overall Score */}
              {movie.explanation?.hybrid_score && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Overall Hybrid Score:</h4>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${((movie.explanation.hybrid_score || 0) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-gray-900">
                      {((movie.explanation.hybrid_score || 0) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              {/* Weights Info */}
              {movie.explanation?.weights && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-2">Algorithm Weights:</p>
                  <div className="flex flex-wrap gap-3">
                    <span className="text-xs flex items-center gap-1">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      Content: {((movie.explanation.weights.content || 0) * 100)}%
                    </span>
                    <span className="text-xs flex items-center gap-1">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Video: {((movie.explanation.weights.video || 0) * 100)}%
                    </span>
                    <span className="text-xs flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Script: {((movie.explanation.weights.script || 0) * 100)}%
                    </span>
                    <span className="text-xs flex items-center gap-1">
                      <span className="w-2 h-2 bg-pink-500 rounded-full"></span>
                      Audio: {((movie.explanation.weights.audio || 0) * 100)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovieCard;